import { useState, useEffect, useCallback } from 'react';

interface LayoutState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  sidebarOpen: boolean;
}

interface LayoutActions {
  toggleSidebar: () => void;
  closeSidebar: () => void;
  openSidebar: () => void;
}

type UseLayoutReturn = LayoutState & LayoutActions;

// Use Tailwind's breakpoints - md: 768px, lg: 1024px
const BREAKPOINTS = {
  mobile: 768,    // md: breakpoint
  tablet: 1024,   // lg: breakpoint
};

export const useLayout = (): UseLayoutReturn => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Simple device detection based on Tailwind breakpoints
  const isMobile = windowWidth < BREAKPOINTS.mobile;
  const isTablet = windowWidth >= BREAKPOINTS.mobile && windowWidth < BREAKPOINTS.tablet;
  const isDesktop = windowWidth >= BREAKPOINTS.tablet;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-close sidebar when switching to desktop
  useEffect(() => {
    if (isDesktop) {
      setSidebarOpen(false);
    }
  }, [isDesktop]);

  // Action functions
  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const openSidebar = useCallback(() => {
    setSidebarOpen(true);
  }, []);

  return {
    // State
    isMobile,
    isTablet,
    isDesktop,
    sidebarOpen,
    
    // Actions
    toggleSidebar,
    closeSidebar,
    openSidebar,
  };
};